import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { MARRIAGE_FAQ_CATEGORIES } from "@/content/marriage-corpus/faq-defs";

export const Route = createFileRoute("/en/marriage/faq/")({
  loader: async (): Promise<{ faqs: { q: string; a: string }[] }> => {
    const m = await import("@/content/marriage-corpus");
    return { faqs: m.MARRIAGE_FAQ_EN };
  },
  component: MarriageFaqHubEn,
  head: ({ loaderData }) => {
    const faqs = loaderData?.faqs ?? [];
    const site = getActiveSiteUrl();
    return {
      ...pageHeadForProvince({
        title: `Marriage Registration in Thailand — FAQ (${faqs.length} Answers)`,
        description:
          "Thai-foreign marriage registration FAQ: embassy affirmation of freedom to marry, Thai translation and MFA legalisation, prenuptial agreements, divorce, surname and title changes, marriage-based visa extensions and birth registration. Referenced to bora.dopa.go.th, consular.mfa.go.th and immigration.go.th.",
        path: "/en/marriage/faq",
        type: "article",
        alternates: [{ lang: "th", path: "/marriage/faq" }],
        keywords: [
          "marriage registration thailand",
          "affirmation of freedom to marry",
          "thai marriage certificate",
          "prenuptial agreement thailand",
          "marriage visa thailand",
        ],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: `${site}/en` },
              { name: "Marriage Registration FAQ", url: `${site}/en/marriage/faq` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/en/marriage/faq")) },
      ],
    };
  },
});

function MarriageFaqHubEn() {
  const { faqs } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span> <span>Marriage Registration FAQ</span>
        </nav>
        <h1 className="mb-3 text-3xl font-bold md:text-4xl">
          Marriage and Civil Registration in Thailand — {faqs.length} FAQs
        </h1>
        <p className="mb-6 text-muted-foreground">
          Answers follow Book V of the Civil and Commercial Code, the marriage-equality amendment in force since
          22 January 2025, and published guidance from the Department of Provincial Administration (bora.dopa.go.th),
          the Department of Consular Affairs (consular.mfa.go.th) and the Immigration Bureau (immigration.go.th).
          This is general information, not case-specific advice, and no authority decision is guaranteed.
        </p>
        <div className="mb-6 rounded-lg border bg-muted/30 p-4 text-sm">
          <strong>ภาษาไทย:</strong>{" "}
          <a href="/marriage/faq" className="text-primary underline">FAQ จดทะเบียนสมรสไทย–ต่างชาติ →</a>
        </div>
        <section aria-label="Marriage FAQ topics" className="mb-8">
          <h2 className="mb-3 text-lg font-semibold">Browse by topic</h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {MARRIAGE_FAQ_CATEGORIES.map((c) => (
              <a key={c.slug} href={`/en/marriage/faq/${c.slug}`} className="rounded-lg border bg-card p-3 text-sm transition hover:border-primary hover:shadow-sm">
                <div className="mb-1 font-semibold">{c.titleEn}</div>
                <div className="line-clamp-2 text-xs text-muted-foreground">{c.descEn}</div>
              </a>
            ))}
          </div>
        </section>
        <div className="space-y-4">
          {faqs.map((item: { q: string; a: string }, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">
                <span className="mr-2 text-muted-foreground">Q{i + 1}.</span>
                {item.q}
              </summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>
        <section className="mt-12">
          <h2 className="mb-3 text-xl font-bold">Related services</h2>
          <ul className="grid gap-2 text-sm md:grid-cols-2">
            <li><a className="text-primary underline" href="/en/translation/faq">Certified translation</a></li>
            <li><a className="text-primary underline" href="/en/consular-legalization/faq">MFA legalisation and Apostille</a></li>
            <li><a className="text-primary underline" href="/en/visa/faq">Visa and extension of stay</a></li>
            <li><a className="text-primary underline" href="/en/notary/faq">Notarial services and powers of attorney</a></li>
          </ul>
        </section>
        <section className="mt-10 rounded-lg border bg-primary/5 p-6">
          <h2 className="mb-2 text-xl font-bold">Still need an answer?</h2>
          <p className="mb-4 text-sm text-muted-foreground">
            Ask our team about document lists, sequencing and timing by phone, LINE or email — Mon-Sat 9:00-18:00.
          </p>
          <a href="/contact" className="inline-block rounded-md bg-primary px-5 py-2 font-medium text-primary-foreground">Contact the team →</a>
        </section>
        <FaqSourceNote topic="marriage" locale="en" />
      </main>
      <SiteFooter />
    </>
  );
}
